// Fork (US-WA-01) — pure helpers behind CreateTemplate.vue.
//
// Named-parameter templates ({{nombre_contacto}}) with Meta compliance
// guardrails: every rule here mirrors a documented Meta rejection reason so
// a template that passes should be accepted on review.

// WhatsApp template language codes supported by Meta (common subset),
// labelled in their native names. Spanish variants first for quick access.
export const TEMPLATE_LANGUAGES = [
  { value: 'es_MX', label: 'Español (México)' },
  { value: 'es', label: 'Español' },
  { value: 'es_AR', label: 'Español (Argentina)' },
  { value: 'es_ES', label: 'Español (España)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en', label: 'English' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'pt_PT', label: 'Português (Portugal)' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'ar', label: 'العربية' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'zh_CN', label: '中文 (简体)' },
  { value: 'zh_TW', label: '中文 (繁體)' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'ru', label: 'Русский' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'pl', label: 'Polski' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'th', label: 'ไทย' },
  { value: 'he', label: 'עברית' },
  { value: 'ro', label: 'Română' },
  { value: 'sv', label: 'Svenska' },
  { value: 'da', label: 'Dansk' },
  { value: 'fi', label: 'Suomi' },
  { value: 'nb', label: 'Norsk (bokmål)' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'cs', label: 'Čeština' },
  { value: 'hu', label: 'Magyar' },
  { value: 'uk', label: 'Українська' },
];

// AUTHENTICATION is intentionally excluded: Meta only approves auth templates
// built with its fixed OTP structure, never with a free-form body.
export const TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY'];

export const BODY_LIMIT = 1024;
export const NAME_LIMIT = 512;
export const WORDS_PER_VARIABLE = 2;

const VARIABLE_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;
const VALID_VARIABLE_NAME = /^[a-z][a-z0-9_]*$/;

// "Nombre del contacto" → "nombre_del_contacto"; "Teléfono" → "telefono".
export const normalizeVariableName = label =>
  String(label)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(?=[0-9])/, 'v');

// Ordered unique variable names as written in the body.
export const bodyVariables = bodyText => {
  const names = [...bodyText.matchAll(VARIABLE_PATTERN)].map(match =>
    match[1].trim()
  );
  return [...new Set(names)];
};

export const insertVariableToken = (bodyText, cursorPosition, variableName) => {
  const token = `{{${variableName}}}`;
  const at =
    cursorPosition === null || cursorPosition === undefined
      ? bodyText.length
      : cursorPosition;
  return {
    text: bodyText.slice(0, at) + token + bodyText.slice(at),
    cursor: at + token.length,
  };
};

// --- Meta compliance guardrails --------------------------------------------
// Returns rule keys (translated by the caller). Empty array = compliant.
export const bodyRuleViolations = bodyText => {
  const violations = [];
  const trimmed = bodyText.trim();
  if (!trimmed) return violations;

  if (trimmed.length > BODY_LIMIT) violations.push('BODY_TOO_LONG');

  const variables = [...trimmed.matchAll(VARIABLE_PATTERN)].map(m =>
    m[1].trim()
  );
  const hasPositional = variables.some(name => /^\d+$/.test(name));
  const named = variables.filter(name => !/^\d+$/.test(name));

  if (hasPositional && named.length) violations.push('MIXED_FORMATS');
  else if (hasPositional) violations.push('POSITIONAL_NOT_ALLOWED');

  if (named.some(name => !VALID_VARIABLE_NAME.test(name)))
    violations.push('INVALID_VARIABLE_NAME');

  if (variables.length) {
    if (/^\{\{\s*[^}]+?\s*\}\}/.test(trimmed))
      violations.push('STARTS_WITH_VARIABLE');
    if (/\{\{\s*[^}]+?\s*\}\}$/.test(trimmed))
      violations.push('ENDS_WITH_VARIABLE');
    if (/\}\}\s*\{\{/.test(trimmed)) violations.push('ADJACENT_VARIABLES');

    const words = trimmed
      .replace(VARIABLE_PATTERN, ' ')
      .split(/\s+/)
      .filter(Boolean);
    if (words.length < variables.length * WORDS_PER_VARIABLE)
      violations.push('TOO_MANY_VARIABLES');
  }

  return violations;
};

export const nameRuleViolations = (name, language, existingTemplates = []) => {
  const violations = [];
  if (!name) return violations;

  if (name.length > NAME_LIMIT) violations.push('NAME_TOO_LONG');
  if (
    existingTemplates.some(
      template => template.name === name && template.language === language
    )
  )
    violations.push('NAME_TAKEN');

  return violations;
};

export const exampleRuleViolations = (variables, exampleValues) => {
  const violations = [];
  const missing = variables.some(name => !(exampleValues[name] || '').trim());
  if (missing) violations.push('EXAMPLE_MISSING');

  const multiline = variables.some(name =>
    /[\r\n\t]/.test(exampleValues[name] || '')
  );
  if (multiline) violations.push('EXAMPLE_MULTILINE');

  return violations;
};

// Meta payload for a named-parameter body component.
export const buildBodyComponent = (bodyText, exampleValues) => {
  const variables = bodyVariables(bodyText);
  const body = { type: 'BODY', text: bodyText };
  if (variables.length) {
    body.example = {
      body_text_named_params: variables.map(name => ({
        param_name: name,
        example: exampleValues[name],
      })),
    };
  }
  return body;
};
